import CallEndIcon from "@mui/icons-material/CallEnd";
import CloseIcon from "@mui/icons-material/Close";
import ComputerIcon from "@mui/icons-material/Computer";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  keyframes,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Orb from "../../../components/Orb";
import { useDictation } from "../../../hooks/useDictation";
import { hostname, parseError } from "../../../utils";

interface TestOutboundCallDialogProps {
  open: boolean;
  onClose: () => void;
  agent: any;
  getToken: () => Promise<string>;
}

type CallMode = "phone" | "browser";
type CallDirection = "inbound" | "outbound";
type CallPhase = "setup" | "ringing" | "connected" | "ended";

interface Message {
  role: "user" | "agent";
  content: string;
  timestamp: Date;
}

// Phone ringing sound - use hostname from browser for whitelabel support
const RINGING_SOUND_URL = `${hostname}/static/media/phone-ringing.mp3`;

// Generate a simple phone ring tone using Web Audio API as fallback
const createRingingTone = (): { start: () => void; stop: () => void } => {
  let audioContext: AudioContext | null = null;
  let oscillator1: OscillatorNode | null = null;
  let oscillator2: OscillatorNode | null = null;
  let gainNode: GainNode | null = null;
  let ringInterval: NodeJS.Timeout | null = null;
  let isRinging = false;

  const start = () => {
    if (isRinging) return;
    isRinging = true;

    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    gainNode.gain.value = 0;

    // US phone ring frequencies: 440 Hz and 480 Hz
    oscillator1 = audioContext.createOscillator();
    oscillator1.frequency.value = 440;
    oscillator1.connect(gainNode);
    oscillator1.start();

    oscillator2 = audioContext.createOscillator();
    oscillator2.frequency.value = 480;
    oscillator2.connect(gainNode);
    oscillator2.start();

    // Ring pattern: 2 seconds on, 4 seconds off
    const ringOn = () => {
      if (gainNode && audioContext) {
        gainNode.gain.setTargetAtTime(0.1, audioContext.currentTime, 0.01);
      }
    };
    const ringOff = () => {
      if (gainNode && audioContext) {
        gainNode.gain.setTargetAtTime(0, audioContext.currentTime, 0.01);
      }
    };

    ringOn();
    setTimeout(ringOff, 2000);

    ringInterval = setInterval(() => {
      ringOn();
      setTimeout(ringOff, 2000);
    }, 6000);
  };

  const stop = () => {
    isRinging = false;
    if (ringInterval) {
      clearInterval(ringInterval);
      ringInterval = null;
    }
    if (oscillator1) {
      oscillator1.stop();
      oscillator1 = null;
    }
    if (oscillator2) {
      oscillator2.stop();
      oscillator2 = null;
    }
    if (audioContext) {
      audioContext.close();
      audioContext = null;
    }
  };

  return { start, stop };
};

const DEFAULT_CALL_CONTEXT = `You are calling on behalf of {{companyName}}. {{companyDescription}}

Your goal is to have a friendly, professional conversation to introduce our services and understand the prospect's needs.`;


const TestOutboundCallDialog: React.FC<TestOutboundCallDialogProps> = ({
  open,
  onClose,
  agent,
  getToken,
}) => {
  const theme = useTheme();

  // Call mode selection
  const [callMode, setCallMode] = useState<CallMode>("browser");
  const [callDirection, setCallDirection] = useState<CallDirection>("inbound");

  // Setup form state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [userInformation, setUserInformation] = useState("");
  const [callContext, setCallContext] = useState(DEFAULT_CALL_CONTEXT);

  // Call state
  const [callPhase, setCallPhase] = useState<CallPhase>("setup");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationContext, setConversationContext] = useState<string>("");
  const [isMuted, setIsMuted] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<{
    openingLine: string;
    conversationContext: string;
  } | null>(null);

  // Audio refs
  const ringingAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringingToneRef = useRef<{ start: () => void; stop: () => void } | null>(null);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const isClosingRef = useRef(false);

  // Streaming TTS refs
  const ttsQueueRef = useRef<string[]>([]);
  const isPlayingTTSRef = useRef(false);
  const sentenceBufferRef = useRef("");
  const firstSentenceQueuedRef = useRef(false);

  // Inactivity timeout ref - managed at dialog level so we can control when it runs
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear inactivity timeout helper
  const clearInactivityTimeout = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  // Dictation hook - using Deepgram for lower latency
  // Disable built-in inactivity timeout (0) - we manage it at dialog level
  const {
    isRecording: isDictating,
    isLoading: isDictationLoading,
    startDictation,
    stopDictation,
    transcript,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      // User is speaking - clear inactivity timeout
      clearInactivityTimeout();

      if (isFinal && text.trim() && callPhase === "connected" && !isMuted && !isProcessing && callMode === "browser") {
        // Stop dictation immediately to prevent duplicate inputs
        stopDictation();
        handleUserInput(text.trim());
      }
    },
    onError: (errorMsg) => {
      setError(errorMsg);
    },
    provider: "deepgram",
    inactivityTimeout: 0, // Disable - we manage timeout at dialog level
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Store stopDictation in a ref so cleanup always has access to latest version
  const stopDictationRef = useRef(stopDictation);
  stopDictationRef.current = stopDictation;

  // Clean up on close
  useEffect(() => {
    if (!open) {
      isClosingRef.current = true;
      stopDictationRef.current();
      resetDialog();
    } else {
      isClosingRef.current = false;
    }
  }, [open]);

  // Auto-start dictation when connected and not muted (browser mode only)
  // Also manage inactivity timeout - only runs when waiting for user input
  useEffect(() => {
    // Don't auto-start if dialog is closing
    if (isClosingRef.current) return;

    if (callMode === "browser") {
      if (callPhase === "connected" && !isMuted && !isDictating && !isAgentSpeaking && !isProcessing) {
        // Starting to listen for user input - start inactivity timeout
        startDictation();
        clearInactivityTimeout();
        inactivityTimeoutRef.current = setTimeout(() => {
          // 20 seconds of no user speech - end the call
          endCall();
          setError("Call ended due to 20 seconds of inactivity");
        }, 20000);
      } else if ((isMuted || isAgentSpeaking || isProcessing) && isDictating) {
        // Agent is speaking or processing - clear timeout and stop dictation
        clearInactivityTimeout();
        stopDictation();
      } else if (isAgentSpeaking || isProcessing) {
        // Agent is speaking/processing but dictation already stopped - ensure timeout is cleared
        clearInactivityTimeout();
      }
    }
  }, [callPhase, isMuted, isAgentSpeaking, isProcessing, isDictating, callMode, clearInactivityTimeout]);

  // Stop dictation and clear timeout when dialog unmounts
  useEffect(() => {
    return () => {
      stopDictationRef.current();
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    };
  }, []);

  const resetDialog = () => {
    // Stop dictation first to ensure mic is released
    stopDictation();
    // Clear inactivity timeout
    clearInactivityTimeout();

    setCallMode("browser");
    setCallDirection("inbound");
    setPhoneNumber("");
    setContactName("");
    setUserInformation("");
    setCallContext(DEFAULT_CALL_CONTEXT);
    setCallPhase("setup");
    setLoading(false);
    setError(null);
    setSuccess(null);
    setMessages([]);
    setConversationContext("");
    setIsMuted(false);
    setIsAgentSpeaking(false);
    setIsProcessing(false);
    setGeneratedContent(null);

    // Stop any audio
    if (ringingAudioRef.current) {
      ringingAudioRef.current.pause();
      ringingAudioRef.current.currentTime = 0;
    }
    // Reset TTS state (clears queue, buffer, stops audio)
    ttsQueueRef.current = [];
    sentenceBufferRef.current = "";
    isPlayingTTSRef.current = false;
    firstSentenceQueuedRef.current = false;
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
    }
  };

  const handleClose = () => {
    // Set closing flag first to prevent auto-restart
    isClosingRef.current = true;

    // Always stop dictation when closing
    stopDictation();

    if (callPhase === "connected") {
      endCall();
    }
    resetDialog();
    onClose();
  };

  const playRingingSound = () => {
    // Try to play the audio file first
    if (!ringingAudioRef.current) {
      ringingAudioRef.current = new Audio(RINGING_SOUND_URL);
      ringingAudioRef.current.loop = true;
    }
    ringingAudioRef.current.play().catch((err) => {
      console.warn("Failed to play ringing sound file, using generated tone:", err);
      // Fallback to generated tone
      if (!ringingToneRef.current) {
        ringingToneRef.current = createRingingTone();
      }
      ringingToneRef.current.start();
    });
  };

  const stopRingingSound = () => {
    if (ringingAudioRef.current) {
      ringingAudioRef.current.pause();
      ringingAudioRef.current.currentTime = 0;
    }
    if (ringingToneRef.current) {
      ringingToneRef.current.stop();
    }
  };

  const playTTSAudio = useCallback(async (text: string): Promise<void> => {
    try {
      setIsAgentSpeaking(true);
      const token = await getToken();

      // Get voice ID and TTS model from agent settings
      const voiceSettings = agent?.channelSettings?.voice || {};
      const voiceId = voiceSettings.elevenLabsVoiceId || "21m00Tcm4TlvDq8ikWAM"; // Default voice
      const ttsModel = voiceSettings.ttsModel || "eleven_flash_v2_5"; // Default TTS model

      const response = await axios.post(
        `${hostname}/voice-ws/elevenlabs/preview`,
        {
          voiceId,
          ttsModel,
          text,
          companyId: agent?.companyId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);

      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
      }

      // Return a promise that resolves when audio finishes playing
      return new Promise((resolve, reject) => {
        ttsAudioRef.current = new Audio(audioUrl);
        ttsAudioRef.current.onended = () => {
          setIsAgentSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        ttsAudioRef.current.onerror = (err) => {
          setIsAgentSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          reject(err);
        };

        ttsAudioRef.current.play().catch((err) => {
          setIsAgentSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          reject(err);
        });
      });
    } catch (err) {
      console.error("Failed to play TTS audio:", err);
      setIsAgentSpeaking(false);
    }
  }, [agent, getToken]);

  // Process the TTS queue - plays sentences one after another
  const processTTSQueue = useCallback(async () => {
    if (isPlayingTTSRef.current || ttsQueueRef.current.length === 0) {
      return;
    }

    isPlayingTTSRef.current = true;
    setIsAgentSpeaking(true);

    while (ttsQueueRef.current.length > 0) {
      const sentence = ttsQueueRef.current.shift();
      if (!sentence || !sentence.trim()) continue;

      try {
        const token = await getToken();
        const voiceSettings = agent?.channelSettings?.voice || {};
        const voiceId = voiceSettings.elevenLabsVoiceId || "21m00Tcm4TlvDq8ikWAM";
        const ttsModel = voiceSettings.ttsModel || "eleven_flash_v2_5";

        // Use streaming endpoint for lower latency
        const response = await fetch(`${hostname}/voice-ws/elevenlabs/preview-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ voiceId, ttsModel, text: sentence, companyId: agent?.companyId }),
        });

        if (!response.ok) continue;

        // Stream audio using MediaSource for real-time playback
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);

        await new Promise<void>((resolve) => {
          const audio = new Audio(audioUrl);
          ttsAudioRef.current = audio;

          audio.onended = () => {
            URL.revokeObjectURL(audioUrl);
            resolve();
          };
          audio.onerror = () => {
            URL.revokeObjectURL(audioUrl);
            resolve();
          };

          audio.play().catch(() => {
            URL.revokeObjectURL(audioUrl);
            resolve();
          });
        });
      } catch (err) {
        console.error("Failed to play TTS segment:", err);
      }
    }

    isPlayingTTSRef.current = false;
    setIsAgentSpeaking(false);
  }, [agent, getToken]);

  // Add text to the TTS buffer, extracting sentences to queue
  // First sentence is sent immediately (with min 35 chars), subsequent sentences are queued as they complete
  const addToTTSBuffer = useCallback((text: string) => {
    sentenceBufferRef.current += text;

    // Extract complete sentences (ending with . ! or ?)
    const sentenceRegex = /[^.!?]*[.!?]+/g;
    let match;
    let lastIndex = 0;

    while ((match = sentenceRegex.exec(sentenceBufferRef.current)) !== null) {
      const sentence = match[0].trim();
      if (sentence.length > 0) {
        // For the first sentence, require at least 35 chars for quality TTS
        if (!firstSentenceQueuedRef.current) {
          if (sentence.length >= 35) {
            ttsQueueRef.current.push(sentence);
            firstSentenceQueuedRef.current = true;
            lastIndex = sentenceRegex.lastIndex;
            // Start processing the first sentence immediately
            processTTSQueue();
          }
          // If first sentence is too short, keep it in buffer and wait for more
          break;
        } else {
          // Subsequent sentences are queued as they complete
          ttsQueueRef.current.push(sentence);
          lastIndex = sentenceRegex.lastIndex;
        }
      }
    }

    // Keep the incomplete part in the buffer
    if (lastIndex > 0) {
      sentenceBufferRef.current = sentenceBufferRef.current.slice(lastIndex);
    }
  }, [processTTSQueue]);

  // Flush any remaining text in the buffer
  const flushTTSBuffer = useCallback(() => {
    if (sentenceBufferRef.current.trim()) {
      ttsQueueRef.current.push(sentenceBufferRef.current.trim());
      sentenceBufferRef.current = "";
      processTTSQueue();
    }
  }, [processTTSQueue]);

  // Reset TTS state
  const resetTTSState = useCallback(() => {
    ttsQueueRef.current = [];
    sentenceBufferRef.current = "";
    isPlayingTTSRef.current = false;
    firstSentenceQueuedRef.current = false;
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
    }
  }, []);

  // Phone call handler (original functionality)
  const handlePhoneCall = async () => {
    if (!phoneNumber.trim()) {
      setError("Phone number is required");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setGeneratedContent(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/agents/${agent.id}/test-outbound-call`,
        {
          companyId: agent.companyId,
          phoneNumber: phoneNumber.trim(),
          contactName: contactName.trim(),
          userInformation: userInformation.trim(),
          callContext: callContext.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccess(
          response.data.message || "Test call initiated successfully!"
        );
        setGeneratedContent({
          openingLine: response.data.openingLine,
          conversationContext: response.data.conversationContext,
        });
      } else {
        setError(response.data.message || "Failed to initiate test call");
      }
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  // Browser call handler
  const startBrowserCall = async () => {
    setLoading(true);
    setError(null);
    setCallPhase("ringing");
    playRingingSound();

    try {
      const token = await getToken();

      // Generate opening line and start browser call session
      const response = await axios.post(
        `${hostname}/agents/${agent.id}/test-browser-call`,
        {
          companyId: agent.companyId,
          callDirection: callDirection,
          // Only send these for outbound calls
          ...(callDirection === "outbound" && {
            contactName: contactName.trim(),
            userInformation: userInformation.trim(),
            callContext: callContext.trim(),
          }),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Simulate ringing for 2-3 seconds
      await new Promise((resolve) => setTimeout(resolve, 2000 + Math.random() * 1000));

      stopRingingSound();
      setCallPhase("connected");

      // Store the conversation context for stateless API calls
      setConversationContext(response.data.conversationContext || "");

      // Add agent's opening line as first message
      const openingLine = response.data.openingLine;
      if (openingLine) {
        const agentMessage: Message = {
          role: "agent",
          content: openingLine,
          timestamp: new Date(),
        };
        setMessages([agentMessage]);

        // Play opening line via TTS
        await playTTSAudio(openingLine);
      }
    } catch (err) {
      stopRingingSound();
      setCallPhase("setup");
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUserInput = async (userText: string) => {
    if (!userText.trim() || isProcessing) return;

    // Set processing to prevent multiple simultaneous calls
    setIsProcessing(true);

    // Reset TTS state for new response
    resetTTSState();

    // Capture current messages for API call (before state update)
    // Convert to the format expected by the backend
    const currentMessages = messages.map((m) => ({
      role: m.role === "agent" ? "assistant" : "user",
      content: m.content,
    }));

    // Add user message to conversation
    const userMessage: Message = {
      role: "user",
      content: userText,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Add empty agent message that we'll stream into
    const agentMessageIndex = messages.length + 1; // Account for the user message we just added
    setMessages((prev) => [...prev, { role: "agent", content: "", timestamp: new Date() }]);

    try {
      const token = await getToken();

      // Use streaming endpoint - send full conversation history for stateless operation
      const response = await fetch(
        `${hostname}/agents/${agent.id}/browser-call-message-stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            companyId: agent.companyId,
            message: userText,
            messages: currentMessages,
            conversationContext: conversationContext,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send message");
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      // Stream the response
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      let fullResponse = "";
      let shouldEndCall = false;
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += value;
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const data = JSON.parse(line);

            if (data.type === "text" && data.content) {
              fullResponse += data.content;
              // Update the agent message in place
              setMessages((prev) => {
                const updated = [...prev];
                updated[agentMessageIndex] = {
                  ...updated[agentMessageIndex],
                  content: fullResponse,
                };
                return updated;
              });
              // Stream text to TTS buffer for progressive audio playback
              addToTTSBuffer(data.content);
            } else if (data.type === "done") {
              shouldEndCall = data.endCall || false;
            } else if (data.type === "error") {
              throw new Error(data.message);
            }
          } catch (parseErr) {
            // Ignore JSON parse errors for incomplete chunks
            if (line.trim()) {
              console.warn("Failed to parse SSE line:", line);
            }
          }
        }
      }

      // Flush any remaining text in the TTS buffer
      flushTTSBuffer();

      // Wait for all TTS to finish before allowing new input
      // Poll until TTS queue is empty and not playing
      while (ttsQueueRef.current.length > 0 || isPlayingTTSRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Check if agent ended the call
      if (shouldEndCall) {
        endCall();
      }
    } catch (err) {
      setError(parseError(err));
      // Remove the empty agent message on error
      setMessages((prev) => prev.filter((_, idx) => idx !== agentMessageIndex));
      resetTTSState();
    } finally {
      setIsProcessing(false);
    }
  };

  const endCall = () => {
    // Prevent auto-restart of dictation after ending call
    isClosingRef.current = true;
    setCallPhase("ended");
    stopDictation();
    resetTTSState();
  };

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  const handleStartCall = () => {
    if (callMode === "phone") {
      handlePhoneCall();
    } else {
      startBrowserCall();
    }
  };

  // Render setup form
  const renderSetupForm = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Test the call functionality for agent &quot;{agent?.name}&quot;.
        Choose to test via your browser or initiate a real phone call.
      </Typography>

      {/* Mode toggle */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={callMode}
          exclusive
          onChange={(_, value) => value && setCallMode(value)}
          fullWidth
          size="small"
        >
          <ToggleButton value="browser">
            <ComputerIcon sx={{ mr: 1 }} />
            Browser Test
          </ToggleButton>
          <ToggleButton value="phone">
            <PhoneOutlined sx={{ mr: 1 }} />
            Phone Call
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {callMode === "browser"
            ? "Test the conversation in your browser using your microphone"
            : "Initiate a real outbound phone call to the specified number"}
        </Typography>
      </Box>

      {/* Inbound/Outbound toggle - only for browser mode */}
      {callMode === "browser" && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Call Direction
          </Typography>
          <ToggleButtonGroup
            value={callDirection}
            exclusive
            onChange={(_, value) => value && setCallDirection(value)}
            fullWidth
            size="small"
          >
            <ToggleButton value="inbound">
              Inbound
            </ToggleButton>
            <ToggleButton value="outbound">
              Outbound
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {callDirection === "inbound"
              ? "Simulate calling the agent (you are the customer)"
              : "Simulate the agent calling a prospect (you play the prospect)"}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Phone number only shown for phone mode */}
        {callMode === "phone" && (
          <TextField
            label="Phone Number"
            placeholder="+1 555 123 4567"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            fullWidth
            required
            helperText="The phone number to call (include country code)"
          />
        )}

        {/* Outbound-specific fields (phone mode is always outbound) */}
        {(callMode === "phone" || callDirection === "outbound") && (
          <>
            <TextField
              label="Contact Name"
              placeholder="John Smith"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              fullWidth
              helperText="Name of the person being called (optional)"
            />

            <TextField
              label="User Information"
              placeholder="CEO at Acme Corp, interested in automation solutions, recently viewed pricing page"
              value={userInformation}
              onChange={(e) => setUserInformation(e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Additional context about the contact (company, role, interests, etc.)"
            />

            <TextField
              label="Call Context"
              value={callContext}
              onChange={(e) => setCallContext(e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="Instructions for the AI agent. Use {{companyName}} and {{companyDescription}} as placeholders."
            />
          </>
        )}
      </Box>

      {/* Show generated content for phone calls */}
      {callMode === "phone" && generatedContent && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Generated Opening Line:
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: "action.hover",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2">
              {generatedContent.openingLine}
            </Typography>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Conversation Context:
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: "action.hover",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {generatedContent.conversationContext}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );

  // Render ringing screen
  const renderRingingScreen = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 300,
        gap: 3,
      }}
    >
      <Avatar
        src={agent?.avatarImage}
        sx={{
          width: 100,
          height: 100,
          animation: "pulse 1.5s infinite",
          "@keyframes pulse": {
            "0%": { transform: "scale(1)", opacity: 1 },
            "50%": { transform: "scale(1.05)", opacity: 0.8 },
            "100%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      />
      <Typography variant="h6">{agent?.name}</Typography>
      <Typography variant="body1" color="text.secondary">
        Calling...
      </Typography>
      <CircularProgress size={24} />
    </Box>
  );

  // Render connected call screen
  const renderConnectedScreen = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Agent header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Avatar src={agent?.avatarImage} sx={{ width: 48, height: 48 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {agent?.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isAgentSpeaking && (
              <>
                <VolumeUpIcon fontSize="small" color="primary" />
                <Typography variant="caption" color="primary">
                  Speaking...
                </Typography>
              </>
            )}
            {isDictating && !isAgentSpeaking && (
              <Typography variant="caption" color="text.secondary">
                Listening...
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Conversation history */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          mb: 2,
          maxHeight: 300,
        }}
      >
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              mb: 3,
            }}
          >
            {msg.role === "user" ? (
              /* User message - gray bubble on right */
              <Box
                sx={{
                  bgcolor: theme.palette.mode === "dark" ? "#303030" : "#f4f4f4",
                  color: theme.palette.text.primary,
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "20px",
                  maxWidth: "85%",
                  fontSize: 16,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {msg.content}
              </Box>
            ) : (
              /* Agent message - left aligned, no bubble */
              <Box
                sx={{
                  color: theme.palette.text.primary,
                  maxWidth: "100%",
                  fontSize: 16,
                  lineHeight: 1.75,
                }}
              >
                {msg.content}
              </Box>
            )}
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Dictation indicator */}
      {isDictating && !isAgentSpeaking && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
          <MicIcon
            fontSize="small"
            sx={{
              color: "primary.main",
              animation: `${keyframes`
                0%, 100% {
                  opacity: 1;
                  transform: scale(1);
                }
                50% {
                  opacity: 0.5;
                  transform: scale(0.9);
                }
              `} 1s ease-in-out infinite`,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {transcript || "Listening..."}
          </Typography>
        </Box>
      )}

      {/* Call controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Orb with audio rings */}
        {(() => {
          const ringColor = "#9c43fe"; // Purple to match orb
          // Get background color - default to white for light mode, dark for dark mode
          const bgColor = theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff";

          const rippleOut = keyframes`
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.6;
            }
            100% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          `;

          const pulseScale = keyframes`
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.08);
            }
          `;

          return (
            <Box sx={{ margin: 4 }}>
              <Box sx={{ position: "relative", width: 100, height: 100 }}>
                {/* Orb with pulse animation when speaking */}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    ...(isAgentSpeaking && {
                      animation: `${pulseScale} 1s ease-in-out infinite`,
                    }),
                  }}
                >
                  <Orb
                    hoverIntensity={0.1}
                    rotateOnHover={false}
                    forceHoverState={isAgentSpeaking}
                    backgroundColor={bgColor}
                  />
                </Box>

                {/* Audio rings - only show when speaking */}
                {isAgentSpeaking && (
                  <>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          border: `2px solid ${alpha("#4cc9f0", 0.6)}`,
                          animation: `${rippleOut} 1.5s ease-out infinite`,
                          animationDelay: `${i * 0.5}s`,
                          pointerEvents: "none",
                          zIndex: 10,
                        }}
                      />
                    ))}
                  </>
                )}
              </Box>
            </Box>
          );
        })()}

        {/* Control buttons */}
        <Box sx={{ display: "flex", gap: 3 }}>
          <IconButton
            onClick={toggleMute}
          sx={{
            width: 56,
            height: 56,
            backgroundColor: isMuted ? theme.palette.error.main : theme.palette.action.hover,
            color: isMuted ? theme.palette.error.contrastText : theme.palette.text.primary,
            "&:hover": {
              backgroundColor: isMuted ? theme.palette.error.dark : theme.palette.action.selected,
            },
          }}
        >
          {isMuted ? <MicOffIcon /> : <MicIcon />}
        </IconButton>
        <IconButton
          onClick={endCall}
          sx={{
            width: 56,
            height: 56,
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.error.dark,
            },
          }}
        >
          <CallEndIcon />
        </IconButton>
        </Box>
      </Box>
    </Box>
  );

  // Render ended screen
  const renderEndedScreen = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 200,
        gap: 2,
      }}
    >
      <Typography variant="h6">Call Ended</Typography>
      {error?.includes("inactivity") ? (
        <Typography variant="body2" color="text.secondary">
          Call ended due to 20 seconds of inactivity
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {messages.length} messages exchanged
        </Typography>
      )}
      <Button
        variant="outlined"
        onClick={() => {
          isClosingRef.current = false; // Reset flag for new call
          setCallPhase("setup");
          setMessages([]);
          setConversationContext("");
          setError(null);
        }}
        sx={{ mt: 2 }}
      >
        Start New Call
      </Button>
    </Box>
  );

  const getButtonText = () => {
    if (loading) {
      return callMode === "phone" ? "Initiating Call..." : "Connecting...";
    }
    return callMode === "phone" ? "Start Phone Call" : "Start Browser Call";
  };

  const isStartDisabled = () => {
    if (loading) return true;
    if (callMode === "phone" && !phoneNumber.trim()) return true;
    return false;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {callPhase === "setup" && "Test Outbound Call"}
        {callPhase === "ringing" && "Connecting..."}
        {callPhase === "connected" && "In Call"}
        {callPhase === "ended" && "Call Summary"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {callPhase === "setup" && renderSetupForm()}
        {callPhase === "ringing" && renderRingingScreen()}
        {callPhase === "connected" && renderConnectedScreen()}
        {callPhase === "ended" && renderEndedScreen()}
      </DialogContent>
      {callPhase === "setup" && (
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : callMode === "phone" ? (
                <PhoneOutlined />
              ) : (
                <ComputerIcon />
              )
            }
            onClick={handleStartCall}
            disabled={isStartDisabled()}
          >
            {getButtonText()}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TestOutboundCallDialog;
